import React, { useState } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { selectEmployees, fetchAllEmployees } from 'src/stores/employeeSlice';
import ApiClient from '../ApiClient';
import { notify } from "../utils/ToastUtils";
import { ChevronLeft, UserCog, Trash2, User, CircleAlert } from 'lucide-react';
import { TextField, InputAdornment, MenuItem, Autocomplete } from '@mui/material';
import { MANAGERS } from '../utils/constants';

const ChangeReportingManager = ({ setCurrentView }) => {
    const dispatch = useDispatch();
    const employees = useSelector(selectEmployees);

    const initialState = {
        empName: "",
        manager: ""
    };

    const [state, setState] = useState(initialState);
    const [disableManager, setDisableManager] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const handleChange = (evt) => {
        const value = evt.target.value;
        setState({
            ...state,
            [evt.target.name]: value
        });
    };

    const updateManager = async () => {
        setSubmitAttempted(true);
        if (state.empName && state.manager) {
            setDisableManager(true);
            try {
                await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/admin/changeReportManager`, {
                    empCode: state.empName,
                    manager: state.manager
                });
                notify.success("Changes updated successfully");
                dispatch(fetchAllEmployees());
                setState(initialState);
                setDisableManager(false);
                setSubmitAttempted(false);
            } catch (error) {
                console.log(error);
                setDisableManager(false);
                let errmsg = (error?.response) ? (error?.response?.data?.message ? error?.response?.data?.message : `${error?.response?.status} - ${error?.response?.statusText}`) : "Something went wrong. Please try again later";
                notify.error(errmsg);
            }
        }
    };

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 xl:p-6 flex flex-col">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4 border-b border-slate-100 pb-3 shrink-0">
                <div className="flex items-center gap-3">
                    <button type="button" onClick={() => { setState(initialState); setSubmitAttempted(false); setCurrentView('main'); }} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors -ml-2">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                    </button>
                    <div className="p-2 bg-blue-50 text-blue-600 rounded-lg border border-blue-100">
                        <UserCog size={20} />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-slate-800">Change Reporting Manager</h2>
                        <p className="text-xs text-slate-500 mt-0.5">Update reporting hierarchy</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button type="button" className="px-5 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm flex items-center gap-2 transition-colors shadow-sm" onClick={() => { setState(initialState); setSubmitAttempted(false); }}>
                        <Trash2 size={16} /> Discard
                    </button>
                    <button type="button" className={`px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm flex items-center gap-2 transition-colors shadow-sm ${disableManager ? 'opacity-50 cursor-not-allowed' : ''}`} onClick={updateManager} disabled={disableManager}>
                        {disableManager ? <><span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>Loading...</> : "Save Changes"}
                    </button>
                </div>
            </div>

            <div className="space-y-6">
                {submitAttempted && (!state.empName || !state.manager) && (
                    <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600">
                        <CircleAlert size={16} />
                        <span>Please complete all required fields.</span>
                    </div>
                )}
                <div>
                    <h3 className="text-blue-600 font-semibold text-sm mb-3">1. Select Employee</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Employee Name <span className="text-red-500">*</span></label>
                            <Autocomplete
                                options={employees?.filter(el => el.STATUS === "Active") || []}
                                getOptionLabel={(option) => `${option.EMP_NAME} - ${option.EMP_ID}`}
                                value={employees?.find(el => el.EMP_CODE === state.empName) || null}
                                onChange={(event, newValue) => {
                                    handleChange({ target: { name: 'empName', value: newValue ? newValue.EMP_CODE : '' } });
                                }}
                                size="small"
                                className="w-full bg-white"
                                sx={{ '& .MuiOutlinedInput-root': { padding: '2px 8px', minHeight: '36px' }, '& .MuiAutocomplete-input': { padding: '4px !important', fontSize: '14px' } }}
                                ListboxProps={{ style: { maxHeight: 250, fontSize: '14px' } }}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params}
                                        error={submitAttempted && !state.empName}
                                        required
                                        placeholder="Search employee by name"
                                        className={submitAttempted && !state.empName ? '[&_fieldset]:!border-red-500' : ''}
                                        InputProps={{ 
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start" sx={{ ml: 1 }}>
                                                        <User size={16} className={submitAttempted && !state.empName ? "text-red-500" : "text-slate-400"}/>
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            )
                                        }} 
                                    />
                                )}
                            />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-1 border-t border-slate-100 pt-4 mt-6">
                    <h3 className="text-blue-600 font-semibold text-sm mb-3">2. Reporting Manager Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Current Manager</label>
                            <TextField size="small" value={
                                (() => {
                                    const currentManagerId = employees?.find(el => el.EMP_CODE === state.empName)?.REPORTING_MANAGER_ID;
                                    const foundManager = MANAGERS.find(m => m.value === currentManagerId);
                                    return foundManager ? foundManager.label : (currentManagerId || '');
                                })()
                            } disabled className="w-full bg-slate-50" InputProps={{ startAdornment: <InputAdornment position="start"><UserCog size={16} className="text-slate-400"/></InputAdornment> }} />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Update Manager <span className="text-red-500">*</span></label>
                            <Autocomplete
                                options={MANAGERS || []}
                                getOptionLabel={(option) => option.label}
                                getOptionDisabled={(option) => employees?.find(el => el.EMP_CODE === state.empName)?.REPORTING_MANAGER_ID === option.value}
                                value={MANAGERS.find(m => m.value === state.manager) || null}
                                onChange={(event, newValue) => {
                                    handleChange({ target: { name: 'manager', value: newValue ? newValue.value : '' } });
                                }}
                                size="small"
                                className="w-full bg-white"
                                sx={{ '& .MuiOutlinedInput-root': { padding: '2px 8px', minHeight: '36px' }, '& .MuiAutocomplete-input': { padding: '4px !important', fontSize: '14px' } }}
                                ListboxProps={{ style: { maxHeight: 250, fontSize: '14px' } }}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params}
                                        error={submitAttempted && !state.manager}
                                        required
                                        placeholder="Select Reporting Manager"
                                        className={submitAttempted && !state.manager ? '[&_fieldset]:!border-red-500' : ''}
                                        InputProps={{ 
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start" sx={{ ml: 1 }}>
                                                        <UserCog size={16} className={submitAttempted && !state.manager ? "text-red-500" : "text-slate-400"}/>
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            )
                                        }} 
                                    />
                                )}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChangeReportingManager;